// src/features/users/components/UserList.tsx

import { useCallback, useMemo, useState } from 'react';
import {
  List,
  TextField,
  EmailField,
  FunctionField,
  useTranslate,
  usePermissions,
  useDataProvider,
  useListContext,
} from 'react-admin';
import { Chip, Box, Typography, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';
import { LoadingState } from '@/components/common/LoadingState';
import { useUserPermissions } from '../hooks/useUserPermissions';
import UserActions from './UserActions'
import { RoleChip } from './RoleChip';
import type { User } from '../types';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider, RaFilterPopover } from '@/components/common/RaFilterPopover';
import { usersHelpConfig } from '@/config/helpConfig';
import { compactListSx } from '@/components/common/compactListSx';
import type { UserRole } from '@/types/models';
import { getVisibleRoles } from '../roles';


/**
 * Get the roles that should be excluded from the list for the current user
 */
const getExcludedRoles = (currentRole: UserRole | undefined): UserRole[] => {
  switch (currentRole) {
    case 'Admin':
      return ['SuperAdmin'];
    case 'GroupAdmin':
      return ['SuperAdmin', 'Admin'];
    case 'HotelAdmin':
      return ['SuperAdmin', 'Admin', 'GroupAdmin'];
    default:
      return [];
  }
};

/**
 * Inner component that renders the datagrid with loading awareness
 */
const UserDatagridContent: React.FC<{ canShow: boolean }> = ({ canShow }) => {
  const translate = useTranslate();
  // RA5: `isPending` is true until the first page arrives (isLoading only while
  // actively fetching); use both so the initial load never renders a dead screen.
  const { isLoading, isPending } = useListContext();

  if (isPending || isLoading) {
    return <LoadingState variant="circular" message={translate('resources.users.list.loading')} />;
  }

  return (
    <CustomDatagrid
      bulkActionButtons={false}
      rowClick={canShow ? 'edit' : undefined}
      primarySource="name"
      secondarySource="email"
    >
      {/* Actions column */}
      <FunctionField
        label=""
        sortable={false}
        render={(record: User) => <UserActions record={record} />}
      />

      {/* Name */}
      <TextField
        source="name"
        label={translate('resources.users.fields.name')}
      />

      {/* Email */}
      <EmailField
        source="email"
        label={translate('resources.users.fields.email')}
      />

      {/* Role */}
      <FunctionField
        source="role"
        label={translate('resources.users.fields.role')}
        sortable={true}
        render={(record: User) => <RoleChip role={record.role} />}
      />

      {/* Assigned Hotels/Groups */}
      <FunctionField
        label={translate('resources.users.fields.assignments')}
        sortable={false}
        render={(record: User) => {
          if (record.role === 'SuperAdmin' || record.role === 'Admin') {
            return (
              <Typography variant="body2" color="text.secondary">
                {translate('resources.users.fields.allAccess')}
              </Typography>
            );
          }

          const groupChips = Object.entries(record.assignedGroupNames || {}).map(([id, name]) => ({
            id,
            name,
            color: 'primary' as const,
          }));

          const hotelChips = Object.entries(record.assignedHotelNames || {}).map(([id, name]) => ({
            id,
            name,
            color: 'secondary' as const,
          }));

          const allChips = [...groupChips, ...hotelChips];

          if (allChips.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                {translate('resources.users.fields.noAssignments')}
              </Typography>
            );
          }

          const maxVisible = 3;
          const visible = allChips.slice(0, maxVisible);
          const remaining = allChips.length - maxVisible;

          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {visible.map(chip => (
                <Chip
                  key={chip.id}
                  label={chip.name}
                  size="small"
                  color={chip.color}
                  variant="outlined"
                />
              ))}
              {remaining > 0 && (
                <Tooltip
                  title={allChips.slice(maxVisible).map(c => c.name).join(', ')}
                  arrow
                >
                  <Chip
                    label={`+${remaining}`}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>
          );
        }}
      />

      {/* Active */}
      <FunctionField
        source="isActive"
        label={translate('resources.users.fields.isActive')}
        sortable={false}
        render={(record: User) => (
          <Chip
            label={record.isActive
              ? translate('resources.users.fields.isActive')
              : translate('resources.users.fields.isInactive')}
            size="small"
            color={record.isActive ? 'success' : 'default'}
            variant="outlined"
          />
        )}
      />
    </CustomDatagrid>
  );
};

/**
 * Users list component
 * Displays all users with filtering and sorting capabilities
 * Backend filters by permissions automatically
 *
 * `embedded`: rendered inside Admin Options — the page owns the
 * FilterPopoverProvider and SectionHeader (DeviceList pattern), so only the
 * List (with its popover filters) is rendered here.
 */
export const UserList = ({ embedded }: { embedded?: boolean }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { canShow, canCreate } = useUserPermissions();
  const { permissions } = usePermissions();
  const currentRole = permissions as UserRole | undefined;

  // Get visible roles for current user
  const visibleRoles = useMemo(() => getVisibleRoles(currentRole), [currentRole]);
  const excludedRoles = useMemo(() => getExcludedRoles(currentRole), [currentRole]);

  // Build permanent filter to exclude roles the user shouldn't see
  const permanentFilter = useMemo(() => {
    if (excludedRoles.length > 0) {
      return { excludeRoles: excludedRoles };
    }
    return {};
  }, [excludedRoles]);

  // Export handler - respects permanent filters (role-based visibility)
  const handleExport = useCallback(async () => {
    try {
      const { data } = await dataProvider.getList('users', {
        filter: permanentFilter,
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) return;
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row =>
          headers.map(h => JSON.stringify((row as Record<string, unknown>)[h] ?? '')).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [dataProvider, permanentFilter]);

  // Filters wrapped in CollapsibleFilterPanel
  const filters = useMemo(() => {
    const roleChoices = visibleRoles.map((role) => ({
      id: role,
      name: translate(`resources.users.roles.${role.charAt(0).toLowerCase() + role.slice(1)}`),
    }));

    const statusChoices = [
      { id: 'true', name: translate('resources.users.fields.isActive') },
      { id: 'false', name: translate('resources.users.fields.isInactive') },
    ];

    return (
      <>
        <CustomTextInputFilter
          source="q"
          label={translate('resources.users.list.search')}
          alwaysOn
        />
        <CustomSelectInputFilter
          source="role"
          label={translate('resources.users.fields.role')}
          choices={roleChoices}
          alwaysOn
        />
        <CustomSelectInputFilter
          source="isActive"
          label={translate('resources.users.fields.status')}
          choices={statusChoices}
        />
      </>
    );
  }, [translate, visibleRoles]);

  const list = (
    <List
      sx={compactListSx}
      filter={permanentFilter}
      actions={false}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      title={translate('resources.users.list.title')}
      empty={false}
    >
      <RaFilterPopover>{filters}</RaFilterPopover>
      <UserDatagridContent canShow={canShow} />
    </List>
  );

  if (embedded) {
    return list;
  }

  return (
    <FilterPopoverProvider>
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <SectionHeader manualScreen="users"
          helpConfig={usersHelpConfig}
          useRaFilters
          showCreate={canCreate}
          onCreateClick={() => navigate('/users/create')}
          showExport
          onExportClick={handleExport}
        />
        {list}
      </Box>
    </FilterPopoverProvider>
  );
};
